/**
 * The minimap every 2D stage shows while zoomed in, on web and on Kai Pro:
 * the whole drawing in miniature in the stage's bottom-left corner with the
 * visible window drawn on it, where a press or a drag moves the view so the
 * point under it is the center. What it shows and where a press takes the
 * view are decided here; each platform draws it and wires its input. Its size
 * and margin live with the zoom/pan envelope (zoomPan.ts), which leaves room
 * to pan the drawing clear of it.
 */
import type { Point } from '@kaiizen/kai-floor-plan';
import { screenToModel, type Viewport } from './viewport';
import { centerViewportOn, isZoomedIn, type MinimapGeometry, minimapGeometry } from './zoomPan';

/** The minimap for a view, or null at the fitted minimum, where there is none. Its window never has a negative size. */
export function minimapLayout(fit: Viewport, viewport: Viewport, stageW: number, stageH: number): MinimapGeometry | null {
  if (!isZoomedIn(viewport, fit)) return null;
  const { window: visible, ...geometry } = minimapGeometry(fit, viewport, stageW, stageH);
  return { ...geometry, window: { ...visible, width: Math.max(0, visible.width), height: Math.max(0, visible.height) } };
}

/** The view after a press or a drag on the minimap at `point` (minimap px): centered on the drawing point under it. */
export function viewportAtMinimapPoint(viewport: Viewport, fit: Viewport, point: Point, stageW: number, stageH: number): Viewport {
  return centerViewportOn(viewport, fit, screenToModel(minimapGeometry(fit, viewport, stageW, stageH).viewport, point), stageW, stageH);
}
